import _ from 'underscore';
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Virtuoso } from 'react-virtuoso';
import { useIsMobileWithBreakpoint } from 'hooks/useIsMobileWithBreakpoint';
import ListItem from '../Item';

import styles from './styles.module.scss';

const LIST_ITEM_HEIGHT_DESKTOP = 40;
const LIST_ITEM_HEIGHT_MOBILE = 30;
const MAX_ITEMS_LIMIT = 10;

const CustomSelectList = ({
	selectedValue,
	options,
	onChange,
	searchable,
	resetOption,
	onSearch,
	placeholder,
	searchValue,
	emptyMessage,
	valueField
}) => {
	const isMobile = useIsMobileWithBreakpoint('mobileMedium');
	const LIST_ITEM_HEIGHT = isMobile
		? LIST_ITEM_HEIGHT_MOBILE
		: LIST_ITEM_HEIGHT_DESKTOP;

	const MAX_LIST_HEIGHT = LIST_ITEM_HEIGHT * MAX_ITEMS_LIMIT;
	const [height, setHeight] = useState(MAX_LIST_HEIGHT);

	const isAnyOptionSelected = resetOption && selectedValue;
	const optionsIsEmpty = _.isEmpty(options);
	const selectCondition = (truthyResult) =>
		isAnyOptionSelected ? truthyResult : 0;
	const optionsWithSelectedLength = options.length + selectCondition(1);
	const initialItemCount =
		optionsWithSelectedLength >= MAX_ITEMS_LIMIT
			? MAX_ITEMS_LIMIT
			: optionsWithSelectedLength;

	const refCallback = (ref) => {
		if (ref) {
			if (optionsWithSelectedLength <= MAX_ITEMS_LIMIT) {
				const contentNodeHeight = ref?.children[0].children[1]?.scrollHeight;
				const getNewHeight = () => {
					if (contentNodeHeight === 0) {
						return contentNodeHeight;
					}

					const calculatedHeight = optionsWithSelectedLength * LIST_ITEM_HEIGHT;
					const contentNodeHeightWithSelected =
						contentNodeHeight + selectCondition(LIST_ITEM_HEIGHT);
					const biggestHeight =
						calculatedHeight > contentNodeHeightWithSelected
							? calculatedHeight
							: contentNodeHeightWithSelected;

					return biggestHeight >= MAX_LIST_HEIGHT
						? MAX_LIST_HEIGHT
						: biggestHeight;
				};
				setHeight(getNewHeight() || height);
			} else {
				const calculatedHeight = optionsWithSelectedLength * LIST_ITEM_HEIGHT;
				const newHeight =
					calculatedHeight >= MAX_LIST_HEIGHT
						? MAX_LIST_HEIGHT
						: calculatedHeight;
				setHeight(newHeight);
			}
		}
	};

	return (
		<div className={styles.list}>
			{searchable && (
				<input
					className={styles.search}
					placeholder={placeholder}
					onChange={onSearch}
					value={searchValue}
				/>
			)}

			{searchValue.length && optionsIsEmpty ? (
				<div className={styles.notFound}>{emptyMessage}</div>
			) : (
				<Virtuoso
					className={styles.virtuoso}
					style={{
						height
					}}
					scrollerRef={refCallback}
					increaseViewportBy={{ bottom: 10 }}
					data={options}
					totalCount={options.length}
					initialItemCount={initialItemCount}
					components={{
						Header: () =>
							isAnyOptionSelected ? (
								<ListItem
									value={resetOption[valueField]}
									title={resetOption.title}
									onChange={onChange}
								/>
							) : null
					}}
					itemContent={(index, data) => {
						const value = data?.[valueField];
						return (
							<ListItem
								key={value || index}
								value={value}
								title={data?.title}
								active={_.isEqual(selectedValue, value)}
								onChange={onChange}
							/>
						);
					}}
				/>
			)}
		</div>
	);
};

CustomSelectList.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			value: PropTypes.string
		})
	).isRequired,
	resetOption: PropTypes.shape({
		title: PropTypes.string.isRequired,
		value: PropTypes.string
	}),
	onChange: PropTypes.func,
	selectedValue: PropTypes.node.isRequired,
	searchable: PropTypes.bool,
	onSearch: PropTypes.func,
	placeholder: PropTypes.string,
	searchValue: PropTypes.string,
	emptyMessage: PropTypes.string,
	valueField: PropTypes.string
};

CustomSelectList.defaultProps = {
	searchable: false,
	placeholder: 'Начните вводить...',
	emptyMessage: 'Ничего не найдено',
	valueField: 'name'
};

export default CustomSelectList;
